
import AdUnit from '../components/AdUnit';
import AffiliateBlock from '../components/AffiliateBlock';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, CalendarClock, BedDouble, SlidersHorizontal, Loader2, List, Map as MapIcon, Star } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { PageHero, FAQList, DisclosureNote } from '../components/shared';
import SaveButton from '../components/SaveButton';
import HotelMap from '../components/HotelMap';
import AdSlot from '../components/AdSlot';
import { HOTELS_PAGE } from '../mock';
import { fetchHotels, fetchHotelFilters, fetchFaqs } from '../api';

const FilterGroup = ({ label, options, value, onChange }) => (
  <div>
    <span className="block text-[12px] font-semibold tracking-[0.12em] uppercase text-[#8a7a63] mb-2">{label}</span>
    <div className="flex flex-wrap gap-2">
      {options.map((o) => {
        const active = value === o.value;
        return (
          <button
            key={o.value}
            onClick={() => onChange(o.value)}
            className={`rounded-full px-4 py-2 text-[14px] font-medium transition-colors ${
              active
                ? 'bg-[#1B1D21] text-white'
                : 'bg-white border border-[#E6D7A2] text-[#45596b] hover:border-[#92722A] hover:text-[#1B1D21]'
            }`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

const Hotels = () => {
  const p = HOTELS_PAGE;
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({ emirates: [], budgets: [], tripTypes: [] });
  const [sel, setSel] = useState({
    emirate: searchParams.get('emirate') || 'all',
    budget: searchParams.get('budget') || 'all',
    tripType: searchParams.get('tripType') || 'all',
  });
  const [hotels, setHotels] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const [faqs, setFaqs] = useState(p.faqs);
  const [view, setView] = useState('list');
  useReveal([hotels.length, loading]);

  useEffect(() => {
    fetchHotelFilters().then(setFilters).catch(() => {});
    fetchFaqs('hotels').then((d) => d.length && setFaqs(d)).catch(() => {});
    fetchHotels({}).then((all) => setFeatured(all.filter((h) => h.featured))).catch(() => {});
  }, []);

  useEffect(() => {
    setSel({
      emirate: searchParams.get('emirate') || 'all',
      budget: searchParams.get('budget') || 'all',
      tripType: searchParams.get('tripType') || 'all',
    });
  }, [searchParams]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (sel.emirate !== 'all') params.emirate = sel.emirate;
    if (sel.budget !== 'all') params.budget = sel.budget;
    if (sel.tripType !== 'all') params.tripType = sel.tripType;
    fetchHotels(params)
      .then(setHotels)
      .catch(() => setHotels([]))
      .finally(() => setLoading(false));
  }, [sel]);

  const emirateOpts = [{ value: 'all', label: 'All emirates' }, ...filters.emirates.map((e) => ({ value: e, label: e }))];
  const budgetOpts = [{ value: 'all', label: 'Any budget' }, ...filters.budgets];
  const tripOpts = [{ value: 'all', label: 'Any trip' }, ...filters.tripTypes.map((t) => ({ value: t, label: t }))];

  return (
    <div className="bg-[#F6F1E3]">
      <PageHero image={p.hero} alt="Infinity pool overlooking Dubai Marina" label={p.label} title={p.title} subtitle={p.subtitle} meta={p.meta} />
      <AdUnit slot="inline" />

      <div className="max-w-6xl mx-auto px-5 py-12">
        <div className="reveal">
          <DisclosureNote>
            <span className="font-semibold text-[#1B1D21]">Affiliate disclosure:</span> Some
            links on this page are affiliate links — if you book through them we may earn a
            small commission at no extra cost to you. This never affects which hotels we
            recommend. See our
          </DisclosureNote>
        </div>

        {/* Featured spotlight carousel */}
        {featured.length > 0 && (
          <section className="reveal mt-8" data-testid="featured-carousel">
            <div className="flex items-center gap-2 mb-4">
              <Star className="w-5 h-5 text-[#B68A35]" />
              <h2 className="font-display text-[22px] font-semibold text-[#1B1D21]">Featured stays</h2>
            </div>
            <div className="flex gap-5 overflow-x-auto pb-3 -mx-1 px-1 snap-x snap-mandatory">
              {featured.map((h) => (
                <article
                  key={h.id}
                  data-testid={`featured-hotel-${h.id}`}
                  className="snap-start shrink-0 w-[300px] sm:w-[340px] group bg-white rounded-2xl overflow-hidden border border-[#B68A35] ring-1 ring-[#B68A35]/30 shadow-[0_18px_40px_-28px_rgba(27,29,33,0.45)] flex flex-col"
                >
                  <div className="relative h-44 overflow-hidden">
                    <Link to={h.slug ? `/hotels/${h.slug}` : '/hotels'}><img src={h.image} alt={h.name} className="img-zoom w-full h-full object-cover" /></Link>
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase rounded-full sunset-gradient text-white px-3 py-1 shadow">
                      <Star className="w-3 h-3" /> Featured
                    </span>
                    <div className="absolute top-3 right-3">
                      <SaveButton item={{ type: 'hotel', ref_id: `hotel-${h.id}`, title: h.name, subtitle: `${h.area}, ${h.emirate}`, to: '/hotels' }} />
                    </div>
                  </div>
                  <div className="p-5 flex flex-col grow">
                    <div className="flex items-center gap-1.5 text-[13px] text-[#2F663C] font-medium">
                      <BedDouble className="w-4 h-4" /> {h.area}, {h.emirate}
                    </div>
                    <h3 className="mt-1.5 font-display text-[19px] font-semibold text-[#1B1D21] leading-snug">{h.slug ? <Link to={`/hotels/${h.slug}`} className="hover:text-[#B68A35] transition-colors">{h.name}</Link> : h.name}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-[#4B4F58] grow line-clamp-2">{h.desc}</p>
                    <div className="mt-3 text-[17px] font-semibold text-[#1B1D21]">{h.priceLabel}</div>
                    <button className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#2F663C]/40 text-[#2F663C] hover:bg-[#2F663C] hover:text-white transition-colors px-5 py-2.5 text-[14px] font-semibold">
                      Check availability on {h.bookingVia} <ExternalLink className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {/* Filter bar */}
        <div className="reveal mt-8 rounded-2xl bg-[#fbefdf] border border-[#E6D7A2] p-6">
          <div className="flex items-center gap-2 mb-5">
            <SlidersHorizontal className="w-5 h-5 text-[#e0564e]" />
            <h2 className="font-display text-[22px] font-semibold text-[#1B1D21]">Find your stay</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            <FilterGroup label="Emirate" options={emirateOpts} value={sel.emirate} onChange={(v) => setSel((s) => ({ ...s, emirate: v }))} />
            <FilterGroup label="Budget" options={budgetOpts} value={sel.budget} onChange={(v) => setSel((s) => ({ ...s, budget: v }))} />
            <FilterGroup label="Trip type" options={tripOpts} value={sel.tripType} onChange={(v) => setSel((s) => ({ ...s, tripType: v }))} />
          </div>
        </div>

        {/* Results */}
        <div className="mt-8 flex items-center justify-between gap-4 flex-wrap">
          <p className="text-[15px] text-[#4B4F58]">
            {loading ? 'Searching…' : `${hotels.length} ${hotels.length === 1 ? 'stay' : 'stays'} found`}
          </p>
          <div className="flex items-center gap-3">
            {(sel.emirate !== 'all' || sel.budget !== 'all' || sel.tripType !== 'all') && (
              <button onClick={() => setSel({ emirate: 'all', budget: 'all', tripType: 'all' })} className="text-[14px] font-semibold text-[#2F663C] hover:text-[#1B1D21]">Clear filters</button>
            )}
            <div className="inline-flex rounded-full border border-[#E6D7A2] bg-white p-1">
              <button
                onClick={() => setView('list')}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[14px] font-semibold transition-colors ${view === 'list' ? 'bg-[#1B1D21] text-white' : 'text-[#45596b]'}`}
              >
                <List className="w-4 h-4" /> List
              </button>
              <button
                onClick={() => setView('map')}
                className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-[14px] font-semibold transition-colors ${view === 'map' ? 'bg-[#1B1D21] text-white' : 'text-[#45596b]'}`}
              >
                <MapIcon className="w-4 h-4" /> Map
              </button>
            </div>
          </div>
        </div>

        {view === 'map' ? (
          <div className="mt-5">
            {loading ? (
              <div className="py-20 flex justify-center text-[#92722A]"><Loader2 className="w-8 h-8 animate-spin" /></div>
            ) : (
              <HotelMap hotels={hotels} />
            )}
            <p className="mt-3 text-[13px] text-[#8a7a63]">Map data © OpenStreetMap contributors. Pins show approximate locations.</p>
          </div>
        ) : loading ? (
          <div className="py-20 flex justify-center text-[#92722A]"><Loader2 className="w-8 h-8 animate-spin" /></div>
        ) : hotels.length === 0 ? (
          <div className="py-16 text-center rounded-2xl border border-dashed border-[#D7BC6D] bg-[#EFE7D2]">
            <p className="text-[16px] text-[#4B4F58]">No stays match those filters. Try widening your search.</p>
          </div>
        ) : (
          <div className="mt-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {hotels.map((h) => (
              <article key={h.id} className={`lift group bg-white rounded-2xl overflow-hidden border shadow-[0_18px_40px_-28px_rgba(27,29,33,0.45)] flex flex-col ${h.featured ? 'border-[#B68A35] ring-1 ring-[#B68A35]/30' : 'border-[#E6D7A2]'}`}>
                <div className="relative h-44 overflow-hidden">
                  <img src={h.image} alt={h.name} className="img-zoom w-full h-full object-cover" />
                  <div className="absolute top-3 right-3">
                    <SaveButton item={{ type: 'hotel', ref_id: `hotel-${h.id}`, title: h.name, subtitle: `${h.area}, ${h.emirate}`, to: '/hotels' }} />
                  </div>
                  {h.featured ? (
                    <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] font-semibold tracking-wide uppercase rounded-full sunset-gradient text-white px-3 py-1 shadow">
                      <Star className="w-3 h-3" /> Featured
                    </span>
                  ) : (
                    <span className="absolute top-3 left-3 text-[11px] font-semibold tracking-wide uppercase rounded-full bg-white/90 text-[#8a5a2e] px-3 py-1">{h.budgetLabel}</span>
                  )}
                </div>
                <div className="p-5 flex flex-col grow">
                  <div className="flex items-center gap-1.5 text-[13px] text-[#2F663C] font-medium">
                    <BedDouble className="w-4 h-4" /> {h.area}, {h.emirate}
                  </div>
                  <h3 className="mt-1.5 font-display text-[19px] font-semibold text-[#1B1D21] leading-snug">{h.name}</h3>
                  <p className="mt-2 text-[14px] leading-relaxed text-[#4B4F58] grow">{h.desc}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {h.tripTypes.map((t) => (
                      <span key={t} className="text-[11px] font-medium rounded-full bg-[#F2ECCF] text-[#8a5a2e] px-2.5 py-0.5">{t}</span>
                    ))}
                  </div>
                  <div className="mt-4 text-[17px] font-semibold text-[#1B1D21]">{h.priceLabel}</div>
                  <button className="mt-3 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#2F663C]/40 text-[#2F663C] hover:bg-[#2F663C] hover:text-white transition-colors px-5 py-2.5 text-[14px] font-semibold">
                    Check availability on {h.bookingVia} <ExternalLink className="w-4 h-4" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* Season note */}
        <section className="reveal mt-14 rounded-2xl bg-[#1B1D21] text-white p-7">
          <div className="flex items-center gap-2 text-[#CBA344]">
            <CalendarClock className="w-5 h-5" />
            <span className="text-[12px] font-semibold tracking-[0.16em] uppercase">Best time to book</span>
          </div>
          <p className="mt-3 text-[17px] leading-relaxed text-[#dbe5ee]">
            UAE hotel pricing swings hard with the season. November to March (cooler, peak
            tourist season) carries a 30–60% premium over June–August, when 45°C+ heat
            keeps most leisure travellers away but business and budget travellers get far
            better rates.
          </p>
        </section>

        <section className="reveal mt-14 max-w-3xl mx-auto">
          <h2 className="font-display text-[28px] font-semibold text-[#1B1D21] mb-5">Frequently asked questions</h2>
          <FAQList faqs={faqs} />
        </section>

        <AdSlot slot="inContent" minH={140} className="reveal mt-12" />
      </div>
      <div className="max-w-7xl mx-auto px-5 pb-14"><AffiliateBlock placement="hotels" title="Book your stay" subtitle="Our hotel booking partners — compare prices, free cancellation on most UAE stays." /></div>
    </div>
  );
};

export default Hotels;
